import { DocumentType } from '@/components/modals/DocumentModal';
import fetcher, {
	FleetFile,
	FleetPaginated,
	FleetQueryParams,
} from '../fetcher';
import {
	AddressMasterData,
	IndustryMasterData,
	MasterData,
} from './master-data';
import { Dayjs } from 'dayjs';
import {
	ApprovalHistory,
	CustomerManagement,
	CustomerShareholder,
} from './types';

export enum BmppGroupRelationship {
	Parent = 1,
	Subsidiary = 2,
	Affiliate = 3,
}

export type MemberType = 'local' | 'corporate' | 'retail' | 'estar';

export type CustomerGroupMember = {
	customerId?: string;
	relationshipId?: BmppGroupRelationship | null;
	memberType?: MemberType | null;
};

export type DefaultPair = {
	id: number;
	name: string;
};

export type FilePayload = {
	id: string;
	name: string;
	path: string;
};

export type FileData = {
	id: number;
	name: string;
	url: string;
	path?: string;
	format: DocumentType;
};

export type CustomerInformationPayload<T extends 'json' | 'form'> = {
	businessEntity: T extends 'json' ? MasterData : number;
	companyType: T extends 'json' ? MasterData : number;
	customerName: string;
	customerType: T extends 'json' ? MasterData : number;
	companyEmail: string;
	companyAddress: string;
	rt: string;
	rw: string;
	picName: string;
	picPosition: T extends 'json' ? MasterData : number;
	additionalInformation: string;
	npwpNo: string;
	status: string;
	deviation?: {
		background: string;
		mitigation: string;
		risk: string;
	};
	source: string;
} & (T extends 'json'
	? {
			id: string;
			customerGroup: MasterData;
			address: AddressMasterData;
			industrySector: IndustryMasterData;
			customerType: MasterData;
			frm?: MasterData;
			fbdh?: MasterData;
			division?: string;
		}
	: {
			companyGroupId: number;
			postalId: number;
			industrySectorId: number;
		});

export type CustomerLegalDocumentPayload<T extends 'json' | 'form'> = {
	id: number;
	documentNo: string;
	file: FleetFile;
	defaultDocumentNo?: string;
	isDocumentNoMandatory?: boolean;
	isDocumentDateMandatory?: boolean;
	isMandatory?: boolean;
} & (T extends 'json'
	? {
			documentDate: string;
			documentType: string;
		}
	: {
			documentDate: Dayjs;
		});

export type CustomerReferralDocumentPayload<T extends 'json' | 'form'> = {
	id: number;
	documentNo: string;
	file: FleetFile;
	defaultDocumentNo?: string;
	isDocumentNoMandatory?: boolean;
	isDocumentDateMandatory?: boolean;
	isMandatory?: boolean;
} & (T extends 'json'
	? {
			documentDate: string;
			documentType: string;
		}
	: {
			documentDate: Dayjs;
		});

export type CustomerBusinessDocumentPayload<T extends 'json' | 'form'> = {
	id: number;
	documentNo: string;
	file: FleetFile;
	defaultDocumentNo?: string;
	isDocumentNoMandatory?: boolean;
	isDocumentDateMandatory?: boolean;
	isMandatory?: boolean;
} & (T extends 'json'
	? {
			documentType: string;
			documentDate: string;
			publisher: MasterData;
			publishers: MasterData[];
			validityPeriod: MasterData;
			validityPeriods: MasterData[];
		}
	: {
			documentDate: Dayjs;
			publisherId: number;
			validityPeriodId: number;
		});

/**
 * @deprecated marked for removal
 */

export type CustomerManagementInfoPayload<T extends 'json' | 'form'> = {
	name: string;
	address: string;
	rt: string;
	rw: string;
	phoneNumber: string;
	identityNumber: string;
} & (T extends 'json'
	? {
			id: number;
			dateOfBirth: string;
			citizenship: MasterData;
			nationality: MasterData;
			identityCardType: MasterData;
			identityFile: FileData;
			position: MasterData;
			status: MasterData;
			postal: AddressMasterData;
		}
	: {
			id?: number;
			dateOfBirth: Dayjs;
			citizenship: number;
			nationalityId: number;
			identityCardType: number;
			identityFile: FilePayload;
			position: number;
			status: number;
			postalId: number;
		});

/**
 * @deprecated marked for removal
 */

export type CustomerShareholderPayload<T extends 'json' | 'form'> = {
	name: string;
	address: string;
	rt: string;
	rw: string;
	phoneNumber: string;
	identityNumber: string;
} & (T extends 'json'
	? {
			id: number;
			citizenship: MasterData;
			nationality: MasterData;
			shareholderType: MasterData;
			identityCardType: MasterData;
			identityFile: FileData;
			status: MasterData;
			postal: AddressMasterData;
			shareholding: number;
		}
	: {
			id?: number;
			citizenship?: number;
			nationalityId?: number;
			shareholderType: number;
			identityCardType: number;
			identityFile: FilePayload;
			status: number;
			postalId: number;
			shareholding: number;
		});

export type CustomerDocument = {
	id: number;
	documentType: string;
	description: string;
	isMandatory: boolean;
	publishers: MasterData[];
	validityPeriod: MasterData[];
};

export type CreateCustomerPayload =
	| {
			action: 'create';
			customerId?: string;
			customerInformation: CustomerInformationPayload<'form'>;
			source?: string;
	  }
	| {
			action: 'update';
			customerId: string;
			customerInformation?: CustomerInformationPayload<'form'>;
			customerLegalDocuments?: CustomerLegalDocumentPayload<'form'>[];
			customerBusinessDocuments?: CustomerBusinessDocumentPayload<'form'>[];
			customerReferralDocuments?: CustomerReferralDocumentPayload<'form'>[];
			customerCompanyManagementInformation?: CustomerManagementInfoPayload<'form'>[];
			customerCompanyShareholderInformation?: CustomerShareholderPayload<'form'>[];
	  }
	| {
			action: 'submit';
			customerId: string;
			customerInformation: CustomerInformationPayload<'form'>;
			customerLegalDocuments?: CustomerLegalDocumentPayload<'form'>[];
			customerBusinessDocuments?: CustomerBusinessDocumentPayload<'form'>[];
			customerReferralDocuments?: CustomerReferralDocumentPayload<'form'>[];
			customerCompanyManagementInformation?: CustomerManagementInfoPayload<'form'>[];
			customerCompanyShareholderInformation?: CustomerShareholderPayload<'form'>[];
	  };

export type CreateCustomerResponse = {
	customerId: string;
	customerLegalDocuments: CustomerLegalDocumentPayload<'json'>[];
	customerBusinessDocuments: CustomerBusinessDocumentPayload<'json'>[];
	customerReferralDocuments: CustomerReferralDocumentPayload<'json'>[];
	customerExpired?: string;
};

export const createCustomer = (payload: CreateCustomerPayload) => {
	return fetcher<CreateCustomerResponse>({
		api: '/customers/create-customer',
		body: payload,
	});
};

export type CustomerPayload = {
	customerId?: string;
	source?: string;
	customerInformation: {
		customerType: number;
		businessEntity: number;
		companyType: number;
		customerName: string;
		companyEmail: string;
		companyAddress: string;
		rt: string;
		rw: string;
		postalId: number;
		industrySectorId: number;
		companyGroupId?: string;
		picName: string;
		npwpNo: string;
		picPosition: number;
		additionalInformation: string;
		deviation: {
			background: string;
			mitigation: string;
			risk: string;
		};
	};
	customerLegalDocuments: {
		id: number;
		file: {
			id: string;
			name: string;
			path: string;
		};
		documentNo: string;
		documentDate: string;
	}[];
	customerBusinessDocuments: {
		id: number;
		file: {
			id: string;
			name: string;
			path: string;
		};
		documentNo: string;
		documentDate: string;
		publisherId: number;
		validityPeriodId: number;
	}[];
	customerReferralDocuments: {
		id: number;
		file: {
			id: string;
			name: string;
			path: string;
		};
		documentNo: string;
		documentDate: string;
	}[];
	customerCompanyShareholderInformation: {
		id?: number;
		customerId?: string;
		name: string;
		citizenship: number;
		nationalityId: number;
		identityCardType: number;
		identityNumber: string;
		address: string;
		rt: string;
		rw: string;
		postalId: number;
		phoneNumber: string;
		shareholding: number;
		status: number;
		shareholderType: number;
		identityFile: {
			id: string;
			name: string;
			path: string;
		};
	}[];
	customerCompanyManagementInformation: {
		id?: number;
		customerId?: string;
		name: string;
		citizenship: number;
		nationalityId: number;
		identityCardType: number;
		identityNumber: string;
		dateOfBirth: string;
		position: number;
		address: string;
		rt: string;
		rw: string;
		postalId: number;
		phoneNumber: string;
		status: number;
		identityFile: {
			id: string;
			name: string;
			path: string;
		};
		isShareholder?: boolean;
		shareholding?: number;
	}[];
	notes?: string;
};

export const createCustomerV2 = (body: CustomerPayload) => {
	return fetcher<string>({
		api: '/v2/customers/create-customer',
		body,
	});
};

export const updateCustomerV2 = (body: CustomerPayload) => {
	return fetcher<string>({
		api: '/v2/customers/update-customer',
		body,
	});
};

export type Customer = {
	createdBy: string;
	createdById: string;
	customerGroup: string;
	customerName: string;
	id: string;
	customerId: string;
	reportTo: string;
	prospects?: {
		prospectId: string;
		prospectStatus: string;
		pendingUser?: string;
	}[];
	expiredAt: string;
	status: string;
	source: string;
	npwp: string;
	division: string;
	createdAt: string;
};

export const getCustomers = (params?: FleetQueryParams) => {
	return fetcher<FleetPaginated<Customer>>({
		api: '/customers/get-list',
		body: params,
	});
};

export const getFSCustomers = (params?: FleetQueryParams) => {
	return fetcher<FleetPaginated<Customer>>({
		api: '/v2/customers/get-waiting-customer-list',
		body: params,
	});
};

export const getCustomerDetail = (id: string) => {
	return fetcher<{
		customerInformation: CustomerInformationPayload<'json'>;
		customerLegalDocuments?: CustomerLegalDocumentPayload<'json'>[];
		customerBusinessDocuments?: CustomerBusinessDocumentPayload<'json'>[];
		customerReferralDocuments?: CustomerReferralDocumentPayload<'json'>[];
		customerCompanyManagementInformation?: CustomerManagement[];
		customerCompanyShareholderInformation?: CustomerShareholder[];
		customerHistory?: {
			date: string;
			event: string;
			fullName: string;
			id: number;
			no: number;
			notes: string;
			userId: number;
			userName: string;
		}[];
		prospects?: {
			no: number;
			progress: string;
			prospectDateAt: string;
			prospectId: string;
			status: string;
			reviewStatus: string;
			progressGroup: {
				value: string;
				id: string;
			};
		}[];
		externalCustomerData: string;
	}>({
		api: '/customers/get-customer-detail',
		body: {
			id,
		},
	});
};

export type ProspectExistingPlafons = {
	no: number;
	customerPlafonId: number;
	customerId: string;
	napNo: string;
	existingFinancingLimit: number;
	financingLimitCharacteristic: string;
	financingLimitType: string;
	outstandingPrincipal: number;
	outstandingFinancingLimit: number;
	status: string;
	expiredDate: string;
	ntf: number;
};

export const getCustomerPlafons = (customerId: string) => {
	return fetcher<ProspectExistingPlafons[]>({
		api: '/customer-plafons/get-list',
		body: {
			customerId,
		},
	});
};

export type CustomerGroupCorporateCustomer = {
	customerId?: string;
	customerName?: string;
	groupId?: string;
	npwpNo?: string;
	jfFinancingLimit?: number;
	njfFinancingLimit?: number;
	totalFinancingLimit?: number;
	jfOutstandingPrincipal?: number;
	njfOutstandingPrincipal?: number;
	totalOutstandingPrincipal?: number;
	relationshipId?: number | null;
	relationshipDescription?: string | null;
};

export type CustomerGroupRetailCustomer = {
	customerId?: string;
	customerName?: string;
	groupId?: string;
	npwpNo?: string;
	totalNtf?: number;
	totalOutstandingPrincipal?: number;
	relationshipId?: number | null;
	relationshipDescription?: string | null;
};

export type CustomerGroupLocalMember = {
	customerId?: string;
	customerName?: string;
	npwpNo?: string;
	relationshipId?: number | null;
	relationshipDescription?: string | null;
	jfFinancingLimit?: number;
	njfFinancingLimit?: number;
	jfOutstandingPrincipal?: number;
	njfOutstandingPrincipal?: number;
	jfNtf?: number;
	njfNtf?: number;
	totalFinancingLimit?: number;
	totalOutstandingPrincipal?: number;
};

export type CustomerGroupEstarMember = {
	groupId?: string;
	customerId?: string;
	customerName?: string;
	npwpNo?: string;
	companyStatus?: string;
	kota?: string;
	osPrincipal?: number;
	maxOsBalance?: number;
	activeAgreement?: number;
	inProcessAgreement?: number;
	relationshipId?: number | null;
	relationshipDescription?: string | null;
};

export type OutstandingPrincipalDetail = {
	agreementDate?: string;
	agreementNo?: string;
	contractStatus?: string;
	name?: string;
	osPrincipal?: number;
};

export type CustomerGroupNoteHistory = {
	username?: string;
	event?: string;
	status?: string;
	date?: string;
	notes?: string;
};

export type CustomerGroup = {
	customerGroupId: number | null;
	customerGroupName: string;
	groupId: number | null;
	groupName: string;
	address: string;
	id: string;
	name: string;
	customerList: {
		corporates?: CustomerGroupCorporateCustomer[];
		retails?: CustomerGroupRetailCustomer[];
		locals?: CustomerGroupLocalMember[];
		estars?: CustomerGroupEstarMember[];
	};
	isActive: boolean;
	companyTreeFile: FileData;
	outstandingPrincipal?: number;
	totalOutstanding?: number;
	outstandingPrincipalDetail: OutstandingPrincipalDetail[];
	startDate?: string;
	totalNtf?: number;
	totalPlafon?: number;
	totalRemainingPlafon?: number;
	totalExposureAmount?: number;
	customerGroupSummary?: CustomerGroupSummary;
	notesHistory?: CustomerGroupNoteHistory[];
};

export type CustomerGroupSummary = {
	financingLimit: number;
	nonRevolvingFinancingLimit: number;
	nonRevolvingNtf: number;
	nonRevolvingOutstandingPrincipal: number;
	nonRevolvingRemainingLimit: number;
	revolvingFinancingLimit: number;
	revolvingNtf: number;
	revolvingOutstandingPrincipal: number;
	revolvingRemainingLimit: number;
	jfFinancingLimit: number;
	jfNonRevolvingFinancingLimit: number;
	jfNonRevolvingNtf: number;
	jfNonRevolvingOutstandingPrincipal: number;
	jfNonRevolvingRemainingLimit: number;
	jfNtf: number;
	jfOutstandingPrincipal: number;
	jfRemainingLimit: number;
	jfRevolvingFinancingLimit: number;
	jfRevolvingNtf: number;
	jfRevolvingOutstandingPrincipal: number;
	jfRevolvingRemainingLimit: number;
	njfFinancingLimit: number;
	njfNonRevolvingFinancingLimit: number;
	njfNonRevolvingNtf: number;
	njfNonRevolvingOutstandingPrincipal: number;
	njfNonRevolvingRemainingLimit: number;
	njfNtf: number;
	njfOutstandingPrincipal: number;
	njfRemainingLimit: number;
	njfRevolvingFinancingLimit: number;
	njfRevolvingNtf: number;
	njfRevolvingOutstandingPrincipal: number;
	njfRevolvingRemainingLimit: number;
	ntf: number;
	outstandingPrincipal: number;
};

export const getCustomerGroups = (params: FleetQueryParams = {}) => {
	return fetcher<FleetPaginated<CustomerGroup>>({
		api: '/customer/group/get-list',
		body: params,
	});
};

export type CustomerGroupDetail = {
	customerGroupId: number;
	customerGroupName: string;
	address: string;
	creditLimit: number;
	isActive: boolean;
};

export const getCustomerGroupDetail = (id: string) => {
	return fetcher<CustomerGroup>({
		api: '/customer/group/get-detail',
		body: {
			id,
		},
	});
};

export type UpdateCustomerGroupPayload = {
	id: string;
	name: string;
	address: string;
	startDate: string;
	isActive: boolean;
	companyTreeFile: FilePayload;
	members?: CustomerGroupMember[];
	notes?: string;
};

export const updateCustomerGroup = (body: UpdateCustomerGroupPayload) => {
	return fetcher<CustomerGroup>({
		api: '/customer/group/update',
		body,
	});
};

export type CreateCustomerGroupPayload = {
	id?: string;
	name: string;
	address: string;
	startDate?: string;
	isActive: boolean;
	companyTreeFile: FilePayload;
	members?: CustomerGroupMember[];
	notes?: string;
};

export const createCustomerGroup = (body: CreateCustomerGroupPayload) => {
	return fetcher({
		api: '/customer/group/create',
		body,
	});
};

// --- Search Customer for Group ---

export type SearchFleetCustomer = {
	customerId?: string;
	customerName?: string;
	npwpNo?: string;
	memberType?: MemberType | null;
	jfFinancingLimitAmount?: number;
	njfFinancingLimitAmount?: number;
	jfNtfAmount?: number;
	njfNtfAmount?: number;
	jfOutstandingPrincipalAmount?: number;
	njfOutstandingPrincipalAmount?: number;
	jfRemainingLimitAmount?: number;
	njfRemainingLimitAmount?: number;
};

export const searchCustomerForGroup = (params: FleetQueryParams) => {
	return fetcher<SearchFleetCustomer[]>({
		api: '/customer/group/search-customer',
		body: params,
	});
};

// --- Get Customer Members ---

export type GetCustomerMemberResponse = {
	customerId?: string;
	customerName?: string;
	npwpNo?: string;
	relationship?: { id: number; value: string } | null;
	jfFinancingLimitAmount?: number;
	njfFinancingLimitAmount?: number;
	jfNtfAmount?: number;
	njfNtfAmount?: number;
	jfOutstandingPrincipalAmount?: number;
	njfOutstandingPrincipalAmount?: number;
	jfRemainingLimitAmount?: number;
	njfRemainingLimitAmount?: number;
};

export const getCustomerMembers = (members: CustomerGroupMember[]) => {
	return fetcher<GetCustomerMemberResponse[]>({
		api: '/customer/group/get-customer-members',
		body: { members },
	});
};

// --- Customer Plafon Detail ---

export type ReferrerCustomerPlafon = {
	unitId?: number;
	unitName?: string;
	unitBuc?: string;
	unitGroup?: string;
	name?: string;
	phoneNumber?: string;
};

export type CustomerPlafonDetail = {
	prospectId?: string;
	prospectPlafonId?: number;
	orderType?: DefaultPair;
	orderSource?: DefaultPair;
	customerName?: string;
	segmentMarket?: DefaultPair;
	referrer?: ReferrerCustomerPlafon;
	limitApplication?: number;
	plafonType?: DefaultPair;
	financingTypeIds?: DefaultPair[];
	financingObjectIds?: DefaultPair[];
};

export const getCustomerPlafonDetail = (customerPlafonId: number) => {
	return fetcher<CustomerPlafonDetail>({
		api: '/customer-plafons/get',
		body: { customerPlafonId },
	});
};

// --- Plafon Summary ---

export type RequestedFinancingLimitPayload = {
	financingLimitAmount?: number;
	plafonType?: string;
	financingLimitType?: string;
	expiredDate?: string;
};

export type RequestedFinancingLimit = {
	mklNo?: string;
	financingLimitAmount?: number;
	plafonType?: string;
	financingLimitType?: string;
	expiredDate?: string;
	ntfAmount?: number;
	outstandingPrincipalAmount?: number;
	outstandingLimitAmount?: number;
};

export type SummaryFinancingLimitDetail = {
	financingLimitType?: string;
	plafonType?: string;
	status?: string;
	financingLimitAmount?: number;
	ntfAmount?: number;
	outstandingPrincipalAmount?: number;
	outstandingLimitAmount?: number;
	obligorAmount?: number;
};

export type SummaryFinancingLimit = {
	totalNtfAmount?: number;
	totalOutstandingPrincipalAmount?: number;
	totalOutstandingLimitAmount?: number;
	totalObligorAmount?: number;
	details?: SummaryFinancingLimitDetail[];
};

export type SummaryGroupPlafonDetail = {
	customerId?: string;
	customerName?: string;
	plafonType?: string;
	status?: string;
	financingLimitAmount: number;
	ntfAmount: number;
	outstandingPrincipalAmount: number;
	outstandingLimitAmount: number;
	obligorAmount: number;
};

export type SummaryGroupPlafon = {
	details?: SummaryGroupPlafonDetail[];
	totalNtfAmount?: number;
	totalOutstandingPrincipalAmount?: number;
	totalOutstandingLimitAmount?: number;
	totalObligorAmount?: number;
};

export type PlafonSummary = {
	requestedFinancingLimits?: RequestedFinancingLimit[];
	summaryFinancingLimit?: SummaryFinancingLimit;
	summaryGroupPlafon?: SummaryGroupPlafon;
	groupBmpp?: string;
	groupBmppId?: string;
	totalOutstandingLimitAmount?: number;
	totalObligorAmount?: number;
};

export const getPlafonSummary = (body: {
	customerId: string;
	requestedFinancingLimitRequests?: RequestedFinancingLimitPayload[];
}) => {
	return fetcher<PlafonSummary>({
		api: '/customer-plafons/get-plafon-summary',
		body,
	});
};

export const getManagementDetail = (id: number) => {
	return fetcher<CustomerManagement>({
		api: '/customers/get-management-detail',
		body: {
			id,
		},
	});
};

export const getShareholderDetail = (id: number) => {
	return fetcher<CustomerShareholder>({
		api: '/customers/get-shareholder-detail',
		body: {
			id,
		},
	});
};

export const addFRMCustomerManagement = (
	body: CustomerManagement<'payload'>,
) => {
	return fetcher<CustomerManagement>({
		api: '/customer-managements/frm/add',
		body,
	});
};

export const updateFRMCustomerManagement = (
	body: CustomerManagement<'payload'>,
) => {
	return fetcher({
		api: '/customer-managements/frm/update',
		body,
	});
};

export const deleteFRMCustomerManagement = (id: number) => {
	return fetcher({
		api: '/customer-managements/frm/delete',
		body: {
			id,
		},
	});
};

export const addFRMCustomerShareholder = (
	body: CustomerShareholder<'payload'>,
) => {
	return fetcher<CustomerShareholder>({
		api: '/customer-shareholders/frm/add',
		body,
	});
};

export const updateFRMCustomerShareholder = (
	body: CustomerShareholder<'payload'>,
) => {
	return fetcher({
		api: '/customer-shareholders/frm/update',
		body,
	});
};

export const deleteFRMCustomerShareholder = (id: number) => {
	return fetcher({
		api: '/customer-shareholders/frm/delete',
		body: {
			id,
		},
	});
};

export type CustomerDocumentList = {
	id: number;
	documentType: string;
	description: string;
	defaultDocumentNo: string;
	isMandatory: boolean;
	isMandatory2: boolean;
	isDocumentDateMandatory: boolean;
	isDocumentNoMandatory: boolean;
	publisher: MasterData;
	validityPeriod: MasterData;
	publishers: MasterData[];
	validityPeriods: MasterData[];
};

export const getCustomerDocumentList = (body: {
	sectorId: number;
	businessEntityId: number;
	customerTypeId: number;
}) => {
	return fetcher<{
		customerLegalDocuments: CustomerDocumentList[];
		customerBusinessDocuments: CustomerDocumentList[];
		customerReferralDocuments: CustomerDocumentList[];
	}>({
		api: '/customer-documents/get-list',
		body,
	});
};

export type CustomerRejectionHistory = {
	customerName: string;
	no: number;
	prospectId?: string;
	rejectedBy?: string;
	rejectedDate?: string;
	rejectedId?: number;
	rejectedProcess?: string;
	rejectedStatus?: string;
};

export type CustomerRejection = {
	id: number;
	customerId: string;
	customerName: string;
	prospectId: string;
	fbdhName: string;
	frmName: string;
	division: string;
	orderSource: string;
	rejectedDate: string;
	rejectedProcess: string;
	rejectedBy: string;
	rejectedCategory: string;
	rejectedReason: string;
	approvalHistory: ApprovalHistory[];
};

export const getCustomerRejectionDetail = (rejectionId: number) => {
	return fetcher<CustomerRejection>({
		api: '/customers/get-customer-rejection-detail',
		body: {
			rejectionId,
		},
	});
};

export type CustomerVerification = {
	customerId: string;
	verificationStatus: number;
	rejectReason?: number;
	verificationNotes: string;
};

export const verifyCustomer = (body: CustomerVerification) => {
	return fetcher({
		api: '/v2/customers/verify-customer',
		body,
	});
};
